import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAddresses, getExchangeDetail, submitExchange } from '@/api';
import type { Address, ExchangeDetail } from '@/api';

export default function ExchangeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<ExchangeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [current, setCurrent] = useState(0); //轮播当前索引
  const [sheetOpen, setSheetOpen] = useState(false); //兑换弹窗
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2000);
  };

  const loadDetail = useCallback(async () => {
    try {
      const res = await getExchangeDetail(Number(id));
      setDetail(res);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  /** 打开兑换弹窗：拉地址列表并默认选中默认地址 */
  const openExchangeSheet = async () => {
    try {
      const list = await getAddresses();
      setAddresses(list);
      const def = list.find((a) => a.isDefault) || list[0];
      setSelectedAddress(def ? def.id : '');
      setSheetOpen(true);
    } catch (err) {
      showToast(err instanceof Error ? err.message : '获取地址失败');
    }
  };

  /** 确认兑换 */
  const handleExchange = async () => {
    if (!detail) return;
    if (!selectedAddress) {
      showToast('请先选择收货地址');
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    try {
      await submitExchange(detail.id, Number(selectedAddress));
      setSheetOpen(false);
      showToast('兑换成功，请等待发货');
      //刷新余额
      await loadDetail();
    } catch (err) {
      showToast(err instanceof Error ? err.message : '兑换失败');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="mobile-container bg-background-50 min-h-screen flex flex-col items-center justify-center">
        <i className="ri-loader-4-line text-primary-400 text-3xl animate-spin mb-3" />
        <p className="text-foreground-400 text-sm">加载中...</p>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="mobile-container bg-background-50 min-h-screen flex flex-col items-center justify-center">
        <i className="ri-wifi-off-line text-foreground-300 text-3xl mb-3 block" />
        <p className="text-foreground-400 text-sm">{error || '商品不存在'}</p>
        <button
          onClick={() => navigate('/voucher-mall')}
          className="mt-4 px-6 py-2 rounded-full bg-primary-500 text-white text-xs font-bold cursor-pointer"
        >
          返回兑换商城
        </button>
      </div>
    );
  }

  const images = detail.images.length > 0 ? detail.images : [''];
  const enough = detail.voucherBalance >= detail.points;

  return (
    <div className="mobile-container bg-background-50 min-h-screen pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-background-200/50">
        <div className="flex items-center px-4 h-12">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-lg flex items-center justify-center text-foreground-700 cursor-pointer hover:bg-background-100 active:scale-95 transition-all"
            aria-label="返回"
          >
            <i className="ri-arrow-left-line text-xl" />
          </button>
          <h1 className="flex-1 text-center text-foreground-950 text-base font-bold tracking-wide">
            商品详情
          </h1>
          <div className="w-9" />
        </div>
      </header>

      {/* 图片轮播（横滑） */}
      <div className="relative bg-white">
        <div
          className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
          onScroll={(e) => {
            const el = e.currentTarget;
            setCurrent(Math.round(el.scrollLeft / el.clientWidth));
          }}
        >
          {images.map((img, idx) => (
            <div key={idx} className="w-full flex-shrink-0 snap-center">
              {img ? (
                <img src={img} alt={detail.name} className="w-full aspect-square object-cover" />
              ) : (
                <div className="w-full aspect-square flex items-center justify-center bg-background-100">
                  <i className="ri-image-line text-foreground-300 text-3xl" />
                </div>
              )}
            </div>
          ))}
        </div>
        {images.length > 1 && (
          <div className="absolute bottom-3 right-3 px-2.5 py-0.5 rounded-full bg-black/40 text-white text-[10px]">
            {current + 1}/{images.length}
          </div>
        )}
      </div>

      {/* 价格与名称 */}
      <div className="bg-white px-4 py-4">
        <div className="flex items-baseline gap-2">
          <i className="ri-coins-line text-yellow-500 text-lg" />
          <span className="text-primary-500 text-2xl font-bold">
            {Number(detail.points).toLocaleString()}
          </span>
          <span className="text-foreground-400 text-xs">代金券</span>
          <span className="text-foreground-300 text-xs line-through ml-2">
            价值 ¥{Number(detail.price).toLocaleString()}
          </span>
        </div>
        <h1 className="text-foreground-950 text-base font-semibold mt-2">{detail.name}</h1>
      </div>

      {/* 商品详情（富文本） */}
      <div className="bg-white mt-2 px-4 py-4">
        <h2 className="text-foreground-900 text-sm font-bold mb-3">商品详情</h2>
        {detail.description ? (
          <div
            className="rich-content text-foreground-600 text-sm leading-relaxed break-words"
            dangerouslySetInnerHTML={{ __html: detail.description }}
          />
        ) : (
          <p className="text-foreground-300 text-sm">暂无详情</p>
        )}
      </div>

      {/* 底部兑换栏 */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-background-200/60 px-4 py-3 flex items-center gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-1">
            <i className="ri-coins-line text-yellow-500 text-sm" />
            <span className="text-foreground-400 text-xs">
              余额 <span className="text-primary-500 font-bold text-sm">{Number(detail.voucherBalance).toLocaleString()}</span> 代金券
            </span>
          </div>
          {!enough && (
            <p className="text-red-500 text-[10px] mt-0.5">余额不足，还差 {Number(detail.points - detail.voucherBalance).toLocaleString()}</p>
          )}
        </div>
        <button
          onClick={openExchangeSheet}
          className="px-8 py-2.5 rounded-full bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-bold cursor-pointer active:scale-95 transition-transform disabled:opacity-50"
        >
          立即兑换
        </button>
      </div>

      {/* 兑换弹窗：选择收货地址 */}
      {sheetOpen && (
        <div className="fixed inset-0 z-50 flex items-end" onClick={() => setSheetOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div
            className="relative w-full bg-white rounded-t-2xl max-h-[70vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3.5 border-b border-background-100">
              <span className="text-foreground-950 text-sm font-bold">选择收货地址</span>
              <i
                className="ri-close-line text-foreground-400 text-xl cursor-pointer"
                onClick={() => setSheetOpen(false)}
              />
            </div>
            <div className="overflow-y-auto flex-1 px-4 py-2">
              {addresses.length === 0 ? (
                <div className="py-10 text-center">
                  <p className="text-foreground-400 text-sm">暂无收货地址</p>
                  <button
                    onClick={() => navigate('/address')}
                    className="mt-3 px-5 py-2 rounded-full bg-primary-500 text-white text-xs font-bold cursor-pointer"
                  >
                    去添加地址
                  </button>
                </div>
              ) : (
                addresses.map((addr) => (
                  <div
                    key={addr.id}
                    onClick={() => setSelectedAddress(addr.id)}
                    className={`flex items-center gap-3 px-3 py-3 rounded-xl mb-2 cursor-pointer border transition-colors ${
                      selectedAddress === addr.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-background-200 bg-background-50'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-foreground-900 text-sm font-medium">{addr.name}</span>
                        <span className="text-foreground-400 text-xs">{addr.phone}</span>
                        {addr.isDefault && (
                          <span className="px-1.5 py-0.5 bg-primary-100 text-primary-600 text-[10px] rounded">
                            默认
                          </span>
                        )}
                      </div>
                      <p className="text-foreground-500 text-xs mt-1 line-clamp-2">
                        {addr.province} {addr.city} {addr.district} {addr.detail}
                      </p>
                    </div>
                    <i
                      className={`ri-checkbox-circle-fill text-lg ${
                        selectedAddress === addr.id ? 'text-primary-500' : 'text-background-300'
                      }`}
                    />
                  </div>
                ))
              )}
            </div>
            {addresses.length > 0 && (
              <div className="px-4 py-3 border-t border-background-100">
                <button
                  onClick={handleExchange}
                  disabled={submitting}
                  className="w-full py-3 rounded-full bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-bold cursor-pointer active:scale-95 transition-transform disabled:opacity-60"
                >
                  {submitting ? '兑换中...' : `确认兑换（消耗 ${Number(detail.points).toLocaleString()} 代金券）`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] px-6 py-3 rounded-xl bg-black/70 text-white text-sm whitespace-nowrap">
          {toast}
        </div>
      )}
    </div>
  );
}
